import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountWriteContext } from '@context/AccountContextContext'
import { BaseModalType } from '@context/ModalsContext'
import { useProductReadContext, useProductWriteContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Modal } from '@ui/components/Modal'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useProductSwitchPath } from '@utils/path'

import { TickmillProductType } from '@model/TickmillProductType'

interface Product {
  title: string
  description: string[]
  buttonText: string
}

interface Props {
  onAccept?: () => void
}

export const RegisterProductModal: BaseModalType<Props> = (props) => {
  const { close, onAccept } = props
  const [isLoading, setIsLoading] = useState(false)
  const apiClient = useApiClient(ClientApiClient)
  const { isDefaultCFDProductType } = useProductReadContext()
  const { refreshAccount } = useAccountWriteContext()
  const locale = useSessionLanguage()
  const { changeProductContext } = useProductWriteContext()
  const { navigateToNextPath } = useProductSwitchPath()

  const { t } = useTranslation()

  const [initialProductType] = useState(() => isDefaultCFDProductType())

  const handleProductSwitch = () => {
    const nextProduct = changeProductContext()
    navigateToNextPath(nextProduct)
  }

  const registerProduct = async () => {
    const productType = initialProductType ? TickmillProductType.ETD : TickmillProductType.CFD
    await apiClient.addTickmillProduct(productType)
    await close()
    await refreshAccount(locale)
    handleProductSwitch()
    onAccept?.()
  }

  const targetProduct = useMemo(() => {
    const products: Record<TickmillProductType.ETD | TickmillProductType.CFD, Product> = {
      [TickmillProductType.ETD]: {
        title: t('Futures Trading with Tickmill'),
        description: [t('Trade exchange traded derivatives such as Futures on Stock Indices')],
        buttonText: t('Trade Futures'),
      },
      [TickmillProductType.CFD]: {
        title: t('Trade CFDs with Tickmill'),
        description: [
          t(
            'Switch to a world class broker that offers CFDs on 1,200+ Instruments, chosen by 300k traders globally'
          ),
          t('To become an CFD-trader, you are required to complete a quick appropriateness test'),
        ],
        buttonText: t('Become CFD Trader'),
      },
    }

    return initialProductType
      ? products[TickmillProductType.ETD]
      : products[TickmillProductType.CFD]
  }, [initialProductType, t])

  const handleConfirm = async () => {
    setIsLoading(true)
    try {
      await registerProduct()
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal>
      <Modal.Header>{targetProduct.title}</Modal.Header>
      <Modal.Body>
        {targetProduct.description.map((text) => (
          <Modal.Body.Text>{text}</Modal.Body.Text>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Not now')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary loading={isLoading} onClick={handleConfirm}>
          {targetProduct.buttonText}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
